import type { TargetingCreateParams, TargetingUpdateParams } from '@openx/types/gqlTypes.ts';
import type { Targeting } from '@openx/types/targeting/targeting.ts';

const transformRenderingContext = (targeting: Targeting | TargetingCreateParams | TargetingUpdateParams) =>
  targeting.rendering_context?.ad_placement?.val
    ? {
        ...targeting.rendering_context,
        ad_placement: {
          ...targeting.rendering_context.ad_placement,
          val: targeting.rendering_context.ad_placement.val ?? '',
        },
        distribution_channel: {
          ...targeting.rendering_context.distribution_channel,
          val: targeting.rendering_context.distribution_channel.val ?? '',
        },
        device_type: {
          ...targeting.rendering_context.device_type,
          desktop_devices: targeting.rendering_context.device_type.desktop_devices ?? null,
          mobile_devices: targeting.rendering_context.device_type.mobile_devices ?? null,
          tv_devices: targeting.rendering_context.device_type.tv_devices ?? null,
        },
      }
    : undefined;

/**
 * For a package update. DAS merges a targeting without `rendering_context` into the stored one key by key (JSON Merge
 * Patch), keeping whatever is left out. Contextual segments are therefore sent with both lists, null standing for an
 * empty one (MGMT accepts no empty list), so removing all or some of them clears them instead of keeping the stored
 * ones. Creates and clones are not merged, so they send the targeting as it is.
 */
export const withContextualSegmentForUpdate = <T extends TargetingUpdateParams>(targeting: T): T => {
  const { contextual_segment } = targeting;

  return {
    ...targeting,
    contextual_segment:
      contextual_segment?.includes?.length || contextual_segment?.excludes?.length
        ? {
            includes: contextual_segment.includes?.length ? contextual_segment.includes : null,
            excludes: contextual_segment.excludes?.length ? contextual_segment.excludes : null,
          }
        : null,
  };
};

export function transformTargetingForMutation(
  targeting?: TargetingCreateParams | null
): TargetingCreateParams | null | undefined;
export function transformTargetingForMutation(
  targeting?: TargetingUpdateParams | null
): TargetingUpdateParams | null | undefined;
export function transformTargetingForMutation(
  targeting?: Targeting | null
): (Targeting & TargetingCreateParams & TargetingUpdateParams) | null | undefined;
export function transformTargetingForMutation(
  targeting?: Targeting | TargetingCreateParams | TargetingUpdateParams | null
): TargetingCreateParams | TargetingUpdateParams | Targeting | null | undefined {
  if (!targeting) return targeting;

  const rendering_context = transformRenderingContext(targeting);

  if (!rendering_context) {
    const { rendering_context: _renderingContext, ...targetingWithoutRenderingContext } = targeting;

    return targetingWithoutRenderingContext as typeof targeting;
  }

  return {
    ...targeting,
    // eslint-disable-next-line @openx/custom/no-todo-without-jira-reference
    // TODO: revisit rendering_context normalization once we align create/update/domain targeting contracts.
    rendering_context,
  };
}
